export const SERVICES = {
  "testosterone-treatment": {
    label: "TRT SERVICE HUB",
    title: "Testosterone replacement therapy, assessed properly.",
    lede: "A specialist pathway from first symptoms to long-term care — structured assessment, evidence-led diagnosis, and treatment only where it is genuinely appropriate.",
    answer:
      "Testosterone replacement therapy (TRT) is a clinician-prescribed treatment for confirmed testosterone deficiency. At Androgen Health it always begins with a proper assessment — symptoms, history and appropriately collected blood tests — because treatment should never follow from a single low result or a questionnaire alone.",
    sections: [
      {
        h2: "What TRT is — and what it is not",
        paras: [
          "Testosterone replacement therapy restores testosterone to a healthy physiological range in men with a confirmed deficiency, using licensed medicines such as gels or injections prescribed by a clinician. Its purpose is to relieve the symptoms of deficiency and protect long-term health — not to enhance performance, and not a shortcut to feeling younger.",
          "Because testosterone is a prescription-only medicine in the UK, it cannot be advertised to the public and is not sold as a product. It is one outcome of a clinical process that starts with listening, testing and diagnosis — and it is not the right answer for everyone.",
        ],
      },
      {
        h2: "The Androgen Health pathway",
        bullets: [
          "Structured symptom and medical history review, completed online.",
          "Appropriate blood testing — screening first, comprehensive morning panels where indicated.",
          "A clinical consultation to interpret results in context and discuss the cause.",
          "A treatment decision made with you, including a clear explanation if treatment is not advised.",
          "If treated: a formulation choice you understand, structured monitoring and planned reviews.",
        ],
      },
      {
        h2: "If treatment is appropriate",
        paras: [
          "You will know exactly what has been prescribed, why, what it costs, and what monitoring follows. Formulation — gel, injection or another clinically appropriate option — is chosen with your clinician based on your physiology, preferences and lifestyle, with the trade-offs of each explained plainly.",
          "Prescriptions are dispensed through compliant pharmacy arrangements with identity checks and full traceability, and delivered discreetly where clinically appropriate.",
        ],
      },
      {
        h2: "If treatment is not appropriate",
        paras: [
          "Some patients complete the full assessment and are advised that TRT is not indicated — perhaps because results don't support the diagnosis, or because risks outweigh benefits. That advice comes with an explanation and, where relevant, alternatives or referral. It is a legitimate outcome of a good assessment, and you will not have been promised treatment by purchasing a test.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I start TRT on the basis of one low blood test?",
        a: "No. A single result — particularly from an unstandardised sample — is not a diagnosis. Confirmation generally requires appropriately timed morning testing, often repeated, interpreted alongside symptoms and history by a clinician.",
      },
      {
        q: "Will Androgen Health prescribe after one consultation?",
        a: "Not automatically. Treatment is offered only where a diagnosis is confirmed and it is clinically appropriate. Assessment fees cover the clinical process, not a prescription.",
      },
      {
        q: "Is this a steroid or bodybuilding service?",
        a: "No. Androgen Health is a specialist clinical service for diagnosed testosterone deficiency. We do not provide performance enhancement, and we apply the same standards of assessment and monitoring whether a patient is an athlete or not.",
      },
    ],
    related: ["trt-eligibility", "trt-treatment-options", "trt-monitoring", "switch-trt-clinic"],
  },

  "testosterone-testing": {
    label: "TESTOSTERONE TESTING",
    title: "The right test, collected the right way.",
    lede: "From a first screening test to a full diagnostic panel — what each measures, how samples are taken, and what the results can and cannot tell you.",
    answer:
      "Testing starts the assessment, it does not finish it. A screening test estimates where your testosterone sits; a comprehensive morning panel, often repeated, is what clinicians use to confirm a deficiency and investigate its cause alongside your symptoms and history.",
    sections: [
      {
        h2: "Screening vs diagnostic testing",
        paras: [
          "An initial screening blood test (proposed £35–£49) gives an early, affordable indication of whether low testosterone is plausible and worth investigating properly. A comprehensive diagnostic panel (proposed £89–£129) goes further — measuring testosterone alongside the other hormones and safety markers a clinician needs to interpret it correctly.",
        ],
      },
      {
        h2: "What a comprehensive panel measures",
        bullets: [
          "Total testosterone — the headline measurement, taken in the morning when it is most reliable.",
          "Free testosterone and SHBG — how much testosterone is biologically available.",
          "LH and FSH — to help distinguish primary from secondary causes.",
          "Full blood count and haematocrit — a key safety marker before and during treatment.",
          "Other markers as clinically indicated — including prostate-related assessment where appropriate.",
        ],
      },
      {
        h2: "Venous vs finger-prick sampling",
        paras: [
          "Venous samples — drawn from a vein by a trained phlebotomist — are the standard for diagnostic decisions: they are the reference method clinicians trust for confirming results. Finger-prick kits are convenient and useful for screening, but results are more sensitive to collection technique and are treated as indicative rather than definitive.",
          "Timing matters for both. Testosterone is highest in the morning, so diagnostic samples are collected then — and a borderline result is typically repeated to confirm it before any diagnosis is made.",
        ],
      },
      {
        h2: "Interpreting results — with a clinician, not an algorithm",
        paras: [
          "Results are reported with units, reference ranges and a clinician's interpretation — never an automated verdict. No diagnosis is made from an isolated number: ranges vary by assay and laboratory, and a 'low' result in a man without symptoms may mean something very different from the same result with symptoms.",
          "Existing results are accepted where clinically valid, reducing unnecessary duplication — tell your clinician what you already have.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is a normal testosterone level?",
        a: "UK laboratories typically quote a reference interval of roughly 8–30 nmol/L for adult men, but the exact range depends on the assay used. Interpretation depends on symptoms, timing of the sample and clinical context — not the number alone.",
      },
      {
        q: "Do I need to fast before a testosterone blood test?",
        a: "Fasting is not usually required for testosterone itself, but it may be for other markers in the same panel. You will be given clear instructions with your booking, and morning collection is recommended.",
      },
      {
        q: "Can I use an existing blood result?",
        a: "Yes — suitable existing results are accepted where clinically valid. Your clinician will confirm whether they are adequate for assessment or whether repeat testing is needed.",
      },
    ],
    related: ["trt-eligibility", "trt-cost", "trt-monitoring"],
  },

  "trt-cost": {
    label: "FEES & PLANS",
    title: "Transparent fees. No hidden extras.",
    lede: "Everything you should expect to pay — plans, assessment fees, and the rules we hold ourselves to on charging. Proposed launch pricing, confirmed before going live.",
    answer:
      "Proposed launch pricing is Foundation (oral tablets) at £99 per month, Precision (creams or depot injections) at £129 per month and Specialist (premium injectables, our most popular plan) at £179 per month. One-off assessment fees run from £35 to £149. Plans are monthly with a clear cancellation policy, and no safety-critical test or review is ever charged as an extra.",
    pricing: true,
    sections: [
      {
        h2: "What the plans are designed to do",
        paras: [
          "The tiers differ in the level of service and monitoring included — never in whether essential safety measures are provided. Every patient on treatment receives scheduled monitoring blood tests, planned reviews and access to the clinical team as a baseline.",
          "Before you commit you will see the formulation options and their costs, the monitoring schedule, and the full expected cost of the first twelve months — not just the headline figure.",
        ],
      },
      {
        h2: "Assessment fees, sold honestly",
        paras: [
          "Where practical we sell the initial diagnostic pathway as a clearly priced package, with inclusions and any confirmatory testing stated upfront. Completing an assessment and being advised that TRT is not suitable is a valid outcome — you are never promised treatment by purchasing a test.",
        ],
      },
      {
        h2: "Cancellations and medication costs",
        paras: [
          "Plans run monthly with a clear cancellation policy and no unnecessary long lock-in. Medication costs are included in the plan price for prescribed formulations, and any formulation-specific cost differences are made clear before you choose. Specialist referrals and external investigations may incur additional fees, which would always be agreed with you first.",
        ],
      },
    ],
    faqs: [
      {
        q: "What will my first 12 months cost?",
        a: "That depends on your pathway: assessment fees (£35–£149 across the components) plus your plan. The exact first-year total for your recommended pathway is published on your treatment summary before you commit.",
      },
      {
        q: "Can I cancel?",
        a: "Yes — plans are monthly with a clear cancellation policy. We avoid unnecessary long lock-in periods.",
      },
      {
        q: "Are there any hidden fees?",
        a: "No. If a clinician judges a test or review necessary for safe prescribing, it is not charged as an extra. Any additional investigation is indicated, agreed with you, and priced before it happens.",
      },
    ],
    related: ["testosterone-testing", "trt-treatment-options", "switch-trt-clinic"],
  },

  "trt-eligibility": {
    label: "ELIGIBILITY",
    title: "Who assessment is for — and who may not need treatment.",
    lede: "Symptoms suggest; they do not diagnose. Here is how eligibility is actually decided, including the situations where TRT may be refused.",
    answer:
      "Assessment is appropriate for men with persistent symptoms consistent with low testosterone — fatigue, low libido, low mood, reduced strength or concentration. Eligibility for treatment is decided only after confirmed testing and clinical review. TRT may be declined entirely in certain situations, including active fertility goals without counselling, untreated severe obstructive sleep apnoea, certain hormone-sensitive cancers, and some cardiovascular or haematological risks.",
    sections: [
      {
        h2: "Who assessment makes sense for",
        bullets: [
          "Men with persistent symptoms consistent with testosterone deficiency.",
          "Men with previously low or borderline results who want a proper diagnostic process.",
          "Men already on TRT elsewhere seeking better structure, monitoring or a second opinion.",
        ],
      },
      {
        h2: "Why symptoms alone are never enough",
        paras: [
          "Fatigue, low mood and reduced libido are extremely common — and have many causes. Obesity, poor sleep, stress, medication and other medical conditions can all produce the same symptoms with normal testosterone levels. This is why the assessment investigates causes rather than reacting to a symptom checklist, and why a screening result is a beginning, not a verdict.",
        ],
      },
      {
        h2: "When TRT may not be appropriate",
        bullets: [
          "You are actively trying to conceive and fertility implications have not been addressed — TRT can suppress sperm production.",
          "Certain hormone-sensitive conditions, including prostate or breast cancer, require specialist involvement before considering treatment.",
          "Untreated severe obstructive sleep apnoea, or unmanaged cardiovascular or haematological risks such as a raised haematocrit.",
          "Where a cause — such as weight, sleep or medication — should be addressed first, or where expectations do not match what evidence-based treatment can deliver.",
        ],
      },
      {
        h2: "Independent advice is part of the service",
        paras: [
          "A clinician may conclude that you do not need TRT — and if so, you will be told plainly, with the reasoning explained. Where the right next step lies outside our service, we will say so and point you to it.",
        ],
      },
    ],
    faqs: [
      {
        q: "I have fatigue and low libido — do I need TRT?",
        a: "Not necessarily. Those symptoms have many possible causes. Assessment — history, examination of the pattern, and appropriate blood tests — establishes whether testosterone deficiency is actually among them.",
      },
      {
        q: "Does age alone make me eligible?",
        a: "No. Testosterone declines gradually with age, but age is a context, not a diagnosis. Treatment decisions follow symptoms plus confirmed testing, at any age.",
      },
      {
        q: "What if I want to have children?",
        a: "Tell your clinician before any treatment decision. TRT can suppress sperm production, and fertility preservation options should be discussed first — this counselling is built into our pathway.",
      },
    ],
    related: ["testosterone-testing", "testosterone-treatment", "safety"],
  },

  "trt-treatment-options": {
    label: "TREATMENT OPTIONS",
    title: "Gels, injections — and choosing with a clinician.",
    lede: "The formulation matters less than the fit. Here is what the main options involve, and how the choice is actually made.",
    answer:
      "The main UK treatment options are transdermal gels and injectable formulations, each with genuine trade-offs in routine, stability and side-effect profile. The right choice depends on your physiology, preferences and lifestyle — decided with your clinician, with costs made clear before you commit.",
    sections: [
      {
        h2: "Testosterone gels",
        paras: [
          "A daily gel applied to the skin keeps levels comparatively steady and is easy to step down from if a change is needed. The trade-offs: daily routine, careful application to avoid transfer to partners or children, and absorption that varies between individuals.",
        ],
      },
      {
        h2: "Testosterone injections",
        paras: [
          "Injections — from shorter-acting to longer-acting depot preparations — offer less frequent dosing and more predictable absorption. The trade-offs: levels can swing between doses, some men notice these peaks and troughs, and injections involve a needle and technique that we support you to manage.",
        ],
      },
      {
        h2: "How the decision is made",
        bullets: [
          "Your blood results, including SHBG, which shapes how you handle each formulation.",
          "Your routine and preferences — daily application versus less frequent dosing.",
          "Tolerability, cost differences between formulations, and what monitoring each requires.",
          "Any fertility considerations, which can shape both choice and timing.",
        ],
      },
      {
        h2: "Formulation transparency",
        paras: [
          "You will see the specific medication options and their cost differences before committing to a plan. If a formulation stops working for you, switching is a clinical decision made with your clinician — not an upsell.",
        ],
      },
    ],
    faqs: [
      {
        q: "Which is better — gel or injections?",
        a: "Neither is universally better. Gels offer steady levels with a daily routine; injections offer less frequent dosing with more level fluctuation. The right choice is individual, and made with your clinician.",
      },
      {
        q: "How long does TRT take to work?",
        a: "Some effects — such as changes in libido — are often noticed within weeks, while changes in body composition and energy typically unfold over months. Your clinician will structure reviews to assess your actual response rather than promising timelines.",
      },
      {
        q: "Can I switch formulations later?",
        a: "Yes. Switching between formulations is a recognised clinical decision, made on response and tolerability with your clinician — including a structured review of how the change is landing.",
      },
    ],
    related: ["trt-monitoring", "testosterone-treatment", "safety"],
  },

  "trt-monitoring": {
    label: "ONGOING MONITORING",
    title: "Treatment is the beginning of the relationship.",
    lede: "What safe long-term TRT actually looks like: scheduled blood tests, planned reviews, and side effects taken seriously.",
    answer:
      "Safe TRT is monitored TRT. Ongoing care means scheduled blood tests — testosterone levels, full blood count and haematocrit, and prostate-related assessment where appropriate — alongside planned clinical reviews of response and tolerability, with clear escalation routes when something is not right.",
    sections: [
      {
        h2: "What is monitored, and why",
        bullets: [
          "Testosterone levels — to confirm you are in the intended range, not above or below it.",
          "Full blood count and haematocrit — testosterone can thicken blood; a rising haematocrit needs action, not a shrug.",
          "Prostate-related assessment where appropriate — including PSA-based monitoring as clinically indicated.",
          "Response and tolerability — symptoms, side effects and whether the plan is still the right one.",
        ],
      },
      {
        h2: "The rhythm of reviews",
        paras: [
          "Monitoring follows a schedule, not a request button: an initial review after starting or adjusting treatment, then periodic planned reviews and blood tests whose frequency depends on your plan and clinical need. Reminders are built into the patient account, and missed reviews trigger follow-up rather than silent prescription renewals.",
        ],
      },
      {
        h2: "What happens when something is off",
        paras: [
          "Abnormal results are routed to the clinical team with defined escalation protocols — including dose adjustment, pause, investigation or referral outside the service when appropriate. Suspected serious adverse effects follow the same discipline. You will always know who is reviewing your results and what happens next.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often will I need blood tests?",
        a: "Typically more frequently in the first months of treatment or after a dose change, then at planned intervals once stable. The exact schedule depends on your plan and your clinician's judgement.",
      },
      {
        q: "What if my haematocrit rises?",
        a: "A raised haematocrit is a recognised effect of TRT and a reason monitoring exists. Depending on severity, it may mean a dose adjustment, a pause or further investigation — decided clinically, with you.",
      },
      {
        q: "What happens if I miss a review?",
        a: "Missed reviews trigger follow-up from the clinical team. Prescriptions are not silently renewed without the monitoring that safe prescribing requires.",
      },
    ],
    related: ["safety", "trt-cost", "testosterone-treatment"],
  },

  "switch-trt-clinic": {
    label: "EXISTING PATIENTS",
    title: "Already on TRT? Transfer with continuity.",
    lede: "A proper transfer pathway for men already receiving treatment elsewhere — verifying history, monitoring and suitability before anything changes.",
    answer:
      "Yes, you can transfer. The transfer assessment (£99–£149) verifies your diagnosis, prescription history and monitoring, and reviews your current regimen for suitability. Existing blood results are accepted where clinically valid, and if anything about your current treatment needs attention, it is addressed before your plan continues with us.",
    sections: [
      {
        h2: "What the transfer assessment covers",
        bullets: [
          "Verification of your diagnosis and the basis on which treatment was started.",
          "Review of prescription history, current formulation and dosing.",
          "Review of your monitoring record — or arrangement of the tests that are missing.",
          "A clinical review of suitability, response and any outstanding risks.",
        ],
      },
      {
        h2: "What we ask for",
        paras: [
          "Bring what you have: recent blood results, prescription records or clinic summaries. Existing results are accepted where clinically valid — the assessment identifies any gaps, and anything essential for safe continued prescribing is arranged as part of the process.",
        ],
      },
      {
        h2: "Treatment continuity, handled carefully",
        paras: [
          "Where treatment is clinically appropriate to continue, prescriptions continue without gap through compliant pharmacy arrangements. Where a change is needed — a formulation adjustment, an investigation, or a pause — it is made deliberately, with a written treatment summary you can share with your GP.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will I have a gap in treatment when I switch?",
        a: "The pathway is designed for continuity. Where treatment is appropriate to continue, prescriptions continue through the transfer — any clinical change is made deliberately, not by accident of admin.",
      },
      {
        q: "What if my previous clinic's monitoring was incomplete?",
        a: "The assessment identifies what is missing, and the necessary tests are arranged as part of transferring. Safe prescribing requires the monitoring that goes with it.",
      },
      {
        q: "Can you become my main point of care?",
        a: "Yes — plans include planned reviews, scheduled monitoring, prescription management and a written treatment summary, with coordination with your GP where appropriate.",
      },
    ],
    related: ["trt-cost", "trt-monitoring", "contact"],
  },

  safety: {
    label: "CLINICAL SAFETY",
    title: "Safety is the product.",
    lede: "Side effects, fertility, contraindications and monitoring — the full picture, in plain English, before you decide anything.",
    answer:
      "TRT carries real risks that are managed through assessment and monitoring: it can suppress sperm production, it can raise haematocrit, and it requires prostate-related assessment where appropriate. Androgen Health treats these not as small print but as the core of the service — with documented protocols, clear escalation routes and a clinician's freedom to decline treatment.",
    sections: [
      {
        h2: "Fertility — discussed before treatment, not after",
        paras: [
          "TRT suppresses the hormonal signal that drives sperm production, which can reduce fertility — sometimes significantly, and not always reversibly. If children are a possibility for you, this must be discussed before treatment starts, including preservation options. This conversation is built into our assessment pathway.",
        ],
      },
      {
        h2: "The known risks, stated plainly",
        bullets: [
          "Raised haematocrit — blood thickening that requires monitoring and, if it rises, clinical action.",
          "Suppression of sperm production — the fertility implication above.",
          "Prostate-related considerations — monitored where appropriate as part of ongoing care.",
          "Cardiovascular context — assessed individually as part of deciding whether treatment is appropriate.",
          "Common manageable effects — acne, fluid retention and mood changes among them — reviewed at planned check-ins.",
        ],
      },
      {
        h2: "Contraindications and escalation",
        paras: [
          "Some situations mean TRT should not be started, or should only proceed with specialist involvement — certain hormone-sensitive cancers, some cardiovascular and haematological conditions, and untreated severe obstructive sleep apnoea among them. Abnormal results and suspected serious adverse effects follow documented escalation protocols, including urgent care guidance and referral outside the service when needed.",
        ],
      },
      {
        h2: "Prescribing standards",
        paras: [
          "Prescribing follows documented assessment and monitoring protocols through compliant workflows, with identity checks and medication traceability through appropriate pharmacy arrangements. Prescription-only medicines are not advertised on this site, and our public-facing materials are reviewed against advertising rules for medicines before launch.",
        ],
      },
    ],
    faqs: [
      {
        q: "What are the main side effects of TRT?",
        a: "Recognised effects include raised haematocrit, suppression of sperm production, acne and fluid retention, with prostate-related and cardiovascular considerations monitored where appropriate. Many effects are manageable — but only when someone is actually looking, which is what structured monitoring is for.",
      },
      {
        q: "Does TRT cause infertility?",
        a: "It can. TRT suppresses the hormonal signal that drives sperm production, sometimes significantly. Fertility intentions and preservation options are discussed before any treatment decision.",
      },
      {
        q: "Who can I complain to?",
        a: "A clear complaints route is published on our contact page, with complaints handled separately from clinical care. Concerns about safety are escalated through documented clinical governance procedures.",
      },
    ],
    related: ["trt-monitoring", "trt-eligibility", "contact"],
  },
};

export const SERVICE_ORDER = [
  "testosterone-treatment",
  "testosterone-testing",
  "trt-cost",
  "trt-eligibility",
  "trt-treatment-options",
  "trt-monitoring",
  "switch-trt-clinic",
  "safety",
];
